import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { darkTheme } from '../../lib/tokens';
import { TableScroll } from '../../components/TableScroll';
import { fetchUsers, type UserListFilters } from './api';
import type { UserListItem } from './types';
import { useAppContextStore } from '../app/appContextStore';
import { QuotaBulkActionBar } from '../quota/QuotaBulkActionBar';
import { useCanWrite } from '../auth/permissions';
import { ALL_APPS } from '../../lib/appConstants';

const PAGE_SIZE = 50;

export function UserListPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserListFilters['role'] | 'ALL'>('ALL');
  const [subscription, setSubscription] = useState<UserListFilters['subscription'] | 'ALL'>('ALL');
  const [status, setStatus] = useState<UserListFilters['status']>('active');
  const [sort, setSort] = useState<UserListFilters['sort']>('createdAt');
  const [order, setOrder] = useState<UserListFilters['order']>('desc');

  // Globaler App-Picker: "Alle Apps" = kein Filter; sonst auf die App scopen.
  const appId = useAppContextStore((s) => s.appId);

  // Cursor-Pagination: pageCursors[i] ist der Cursor für Seite i+1 (Seite 1 = undefined).
  // "Zurück" springt einfach im Array zurück statt einen Cursor rückwärts aufzulösen —
  // die Backend-Cursor sind nur vorwärts gültig (nextCursor).
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageCursors([undefined]);
    setPageIndex(0);
  }, [search, role, subscription, status, sort, order, appId]);

  const filters = useMemo<UserListFilters>(
    () => ({
      search: search.trim() || undefined,
      role: role === 'ALL' ? undefined : role,
      subscription: subscription === 'ALL' ? undefined : subscription,
      status,
      sort,
      order,
      limit: PAGE_SIZE,
      cursor: pageCursors[pageIndex],
      app: appId === ALL_APPS ? undefined : appId,
    }),
    [search, role, subscription, status, sort, order, appId, pageCursors, pageIndex],
  );

  const query = useQuery({
    queryKey: ['users', filters],
    queryFn: () => fetchUsers(filters),
  });

  function goToNextPage(): void {
    const nextCursor = query.data?.nextCursor;
    if (!nextCursor) return;
    setPageCursors((prev) => [...prev.slice(0, pageIndex + 1), nextCursor]);
    setPageIndex((i) => i + 1);
  }

  function goToPrevPage(): void {
    setPageIndex((i) => Math.max(0, i - 1));
  }

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-4 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Benutzer-Verwaltung</h1>
          <p className="mt-1 text-xs text-text-tertiary">
            Seite {pageIndex + 1} · {query.data?.items.length ?? 0} Treffer
            {query.data?.hasMore ? ' auf dieser Seite (weitere vorhanden)' : ''}
          </p>
        </div>
      </header>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        role={role}
        onRoleChange={setRole}
        subscription={subscription}
        onSubscriptionChange={setSubscription}
        status={status}
        onStatusChange={setStatus}
        sort={sort}
        order={order}
        onSortChange={(s, o) => {
          setSort(s);
          setOrder(o);
        }}
      />

      {query.isLoading && <SkeletonTable />}
      {query.error && (
        <div
          className="mt-4 rounded-md p-4 text-sm"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
        >
          Fehler: {query.error instanceof Error ? query.error.message : 'unbekannt'}
        </div>
      )}
      {query.data && <UsersTable items={query.data.items} />}

      {query.data && (pageIndex > 0 || query.data.hasMore) && (
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            onClick={goToPrevPage}
            disabled={pageIndex === 0}
            className="rounded-md border px-3 py-1.5 text-xs font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
            style={{ borderColor: darkTheme.colors.borderSubtle, background: darkTheme.colors.bgSurface }}
          >
            ← Zurück
          </button>
          <span className="text-xs text-text-tertiary">Seite {pageIndex + 1}</span>
          <button
            type="button"
            onClick={goToNextPage}
            disabled={!query.data.hasMore || query.isFetching}
            className="rounded-md border px-3 py-1.5 text-xs font-medium text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
            style={{ borderColor: darkTheme.colors.borderSubtle, background: darkTheme.colors.bgSurface }}
          >
            Weiter →
          </button>
        </div>
      )}
    </div>
  );
}

interface FilterBarProps {
  search: string;
  onSearchChange: (v: string) => void;
  role: UserListFilters['role'] | 'ALL';
  onRoleChange: (v: UserListFilters['role'] | 'ALL') => void;
  subscription: UserListFilters['subscription'] | 'ALL';
  onSubscriptionChange: (v: UserListFilters['subscription'] | 'ALL') => void;
  status: UserListFilters['status'];
  onStatusChange: (v: UserListFilters['status']) => void;
  sort: UserListFilters['sort'];
  order: UserListFilters['order'];
  onSortChange: (sort: UserListFilters['sort'], order: UserListFilters['order']) => void;
}

function FilterBar(props: FilterBarProps): JSX.Element {
  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-2 rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <input
        type="search"
        placeholder="E-Mail oder Name…"
        value={props.search}
        onChange={(e) => props.onSearchChange(e.target.value)}
        className="flex-1 min-w-[220px] rounded-md border bg-bg-base px-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent"
        style={{ borderColor: darkTheme.colors.borderSubtle }}
      />

      <Select
        value={props.role ?? 'ALL'}
        onChange={(v) => props.onRoleChange(v as UserListFilters['role'] | 'ALL')}
        options={[
          { value: 'ALL', label: 'Alle Rollen' },
          { value: 'USER', label: 'User' },
          { value: 'ADMIN_VIEWER', label: 'Admin (nur Lesen)' },
          { value: 'REVIEWER', label: 'Reviewer' },
          { value: 'ADMIN', label: 'Admin' },
        ]}
      />

      <Select
        value={props.subscription ?? 'ALL'}
        onChange={(v) => props.onSubscriptionChange(v as UserListFilters['subscription'] | 'ALL')}
        options={[
          { value: 'ALL', label: 'Alle Subs' },
          { value: 'FREE', label: 'Free' },
          { value: 'TRIAL', label: 'Trial' },
          { value: 'PREMIUM', label: 'Premium' },
          { value: 'CANCELLED', label: 'Cancelled' },
        ]}
      />

      <Select
        value={props.status ?? 'active'}
        onChange={(v) => props.onStatusChange(v as UserListFilters['status'])}
        options={[
          { value: 'active', label: 'Aktiv' },
          { value: 'suspended', label: 'Suspendiert' },
          { value: 'deleted', label: 'Gelöscht' },
        ]}
      />

      <Select
        value={`${props.sort}:${props.order}`}
        onChange={(v) => {
          const [s, o] = v.split(':') as [UserListFilters['sort'], UserListFilters['order']];
          props.onSortChange(s, o);
        }}
        options={[
          { value: 'createdAt:desc', label: 'Neuste zuerst' },
          { value: 'createdAt:asc', label: 'Älteste zuerst' },
          { value: 'lastSeenAt:desc', label: 'Zuletzt aktiv' },
          { value: 'email:asc', label: 'Email A→Z' },
        ]}
      />
    </div>
  );
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

function Select({ value, onChange, options }: SelectProps): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border bg-bg-base px-2 py-1.5 text-xs text-text-primary outline-none focus:border-accent"
      style={{ borderColor: darkTheme.colors.borderSubtle }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function UsersTable({ items }: { items: UserListItem[] }): JSX.Element {
  // Mehrfachauswahl fuer Kontingent-Aktionen (CUR-523). Die Auswahl lebt hier
  // und nicht in der Seite: sie gilt genau fuer die gerade sichtbare Trefferliste
  // und soll beim Filterwechsel verschwinden, nicht heimlich weitergetragen
  // werden — sonst vergibt jemand ein Kontingent an Nutzer, die er nicht mehr
  // sieht.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Die Auswahl-Kaestchen existieren nur fuer die Kontingent-Aktion. Ohne
  // Schreibrecht waeren sie eine Auswahl ohne Zweck — also weg (CUR-995).
  const canWrite = useCanWrite();
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allVisible = items.length > 0 && items.every((u) => selected.has(u.id));
  if (items.length === 0) {
    return (
      <div
        className="mt-4 rounded-md border p-12 text-center text-sm text-text-secondary"
        style={{
          background: darkTheme.colors.bgSurface,
          borderColor: darkTheme.colors.borderSubtle,
        }}
      >
        Keine Treffer für die aktuellen Filter.
      </div>
    );
  }

  return (
    <>
      {canWrite && (
        <QuotaBulkActionBar
          selected={selected}
          onDone={() => setSelected(new Set())}
        />
      )}

      {/* Mobile (< md): gestapelte Karten — Daumen-tauglich, kein Quer-Scroll */}
      <ul className="mt-4 space-y-2 md:hidden">
        {items.map((u) => (
          <li key={u.id} className="flex items-start gap-2">
            {canWrite && (
              <input
                type="checkbox"
                aria-label={`${u.email} auswählen`}
                checked={selected.has(u.id)}
                onChange={() => toggle(u.id)}
                className="mt-4"
              />
            )}
            <div className="min-w-0 flex-1">
              <UserCard user={u} />
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop (≥ md): Tabelle, horizontal scrollbar als Sicherheitsnetz */}
      <TableScroll
        className="mt-4 hidden rounded-md border border-border-subtle bg-bg-surface md:block"
        minWidthClass="min-w-[720px]"
      >
        <table className="w-full text-sm">
          <thead>
            <tr
              className="text-left text-[11px] font-semibold uppercase tracking-wide text-text-tertiary"
              style={{ borderBottom: `1px solid ${darkTheme.colors.borderSubtle}` }}
            >
              {canWrite && (
                <th className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label="Alle sichtbaren auswählen"
                    checked={allVisible}
                    onChange={() =>
                      setSelected(allVisible ? new Set() : new Set(items.map((u) => u.id)))
                    }
                  />
                </th>
              )}
              <Th>Name / E-Mail</Th>
              <Th>Role</Th>
              <Th>Subscription</Th>
              <Th>Erstellt</Th>
              <Th>Aktiv</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                selected={selected.has(u.id)}
                onToggle={() => toggle(u.id)}
              />
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function UserCard({ user }: { user: UserListItem }): JSX.Element {
  return (
    <Link
      to={`/users/${user.id}`}
      className="block rounded-md border p-3 transition-colors hover:bg-bg-elevated"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="flex items-center gap-3">
        <Avatar email={user.email} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-text-primary">
            {user.displayName ?? '—'}
          </div>
          <div className="truncate text-xs text-text-tertiary">{user.email}</div>
        </div>
        <StatusChip
          suspendedAt={user.suspendedAt}
          deletedAt={user.deletedAt}
          emailVerified={user.emailVerified}
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-text-secondary">
        <RoleChip role={user.role} />
        <SubscriptionChip
          tier={user.subscription?.tier ?? 'FREE'}
          isTrialActive={user.subscription?.isTrialActive ?? false}
          expiresAt={user.subscription?.expiresAt ?? null}
        />
        <span className="text-text-tertiary opacity-50">·</span>
        <ActivityDot lastSeenAt={user.lastSeenAt} />
      </div>
    </Link>
  );
}

function Th({ children }: { children: React.ReactNode }): JSX.Element {
  return <th className="px-4 py-2 font-semibold">{children}</th>;
}

function UserRow({
  user,
  selected,
  onToggle,
}: {
  user: UserListItem;
  selected: boolean;
  onToggle: () => void;
}): JSX.Element {
  // Muss dieselbe Bedingung sein wie die Kopfzelle oben, sonst verrutscht die
  // Tabelle um eine Spalte.
  const canWrite = useCanWrite();
  return (
    <tr
      className="hover:bg-bg-elevated"
      style={{ borderBottom: `1px solid ${darkTheme.colors.borderSubtle}` }}
    >
      {canWrite && (
        <td className="px-4 py-3">
          <input
            type="checkbox"
            aria-label={`${user.email} auswählen`}
            checked={selected}
            onChange={onToggle}
          />
        </td>
      )}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar email={user.email} />
          <div className="min-w-0">
            <Link
              to={`/users/${user.id}`}
              className="block truncate font-medium text-text-primary hover:underline"
            >
              {user.displayName ?? '—'}
            </Link>
            <div className="truncate text-xs text-text-tertiary">{user.email}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <RoleChip role={user.role} />
      </td>
      <td className="px-4 py-3">
        <SubscriptionChip
          tier={user.subscription?.tier ?? 'FREE'}
          isTrialActive={user.subscription?.isTrialActive ?? false}
          expiresAt={user.subscription?.expiresAt ?? null}
        />
      </td>
      <td className="px-4 py-3 text-xs text-text-secondary">
        {formatRelative(user.createdAt)}
      </td>
      <td className="px-4 py-3 text-xs text-text-secondary">
        <ActivityDot lastSeenAt={user.lastSeenAt} />
      </td>
      <td className="px-4 py-3">
        <StatusChip
          suspendedAt={user.suspendedAt}
          deletedAt={user.deletedAt}
          emailVerified={user.emailVerified}
        />
      </td>
    </tr>
  );
}

function Avatar({ email }: { email: string }): JSX.Element {
  const initial = email.charAt(0).toUpperCase();
  return (
    <div
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
      style={{
        background: `linear-gradient(135deg, ${darkTheme.colors.accent}, ${darkTheme.colors.accentLight})`,
      }}
    >
      {initial}
    </div>
  );
}

// ADMIN_VIEWER waere als Chip zu lang und zu leicht mit ADMIN zu verwechseln.
const ROLE_CHIP_LABEL: Partial<Record<UserListItem['role'], string>> = {
  ADMIN_VIEWER: 'nur Lesen',
};

function RoleChip({ role }: { role: UserListItem['role'] }): JSX.Element {
  const colors = role === 'ADMIN'
    ? { bg: darkTheme.colors.accentSubtle, color: darkTheme.colors.accentLight }
    : role === 'REVIEWER'
    ? { bg: darkTheme.colors.infoBg, color: darkTheme.colors.info }
    : { bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textSecondary };

  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: colors.bg, color: colors.color }}
    >
      {ROLE_CHIP_LABEL[role] ?? role}
    </span>
  );
}

function SubscriptionChip({
  tier,
  isTrialActive,
  expiresAt,
}: {
  tier: string;
  isTrialActive: boolean;
  expiresAt: string | null;
}): JSX.Element {
  const isExpired = expiresAt && new Date(expiresAt).getTime() < Date.now();
  let label = 'Free';
  let bg = darkTheme.colors.bgElevated;
  let color = darkTheme.colors.textSecondary;

  if (isTrialActive) {
    label = 'Trial';
    bg = darkTheme.colors.infoBg;
    color = darkTheme.colors.info;
  } else if (tier !== 'FREE' && !isExpired) {
    label = tier.replace('PREMIUM_PLUS_', '+').replace('PREMIUM_', '');
    bg = darkTheme.colors.successBg;
    color = darkTheme.colors.success;
  } else if (tier !== 'FREE' && isExpired) {
    label = 'Cancelled';
    bg = darkTheme.colors.dangerBg;
    color = darkTheme.colors.danger;
  }

  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: bg, color }}
    >
      {label}
    </span>
  );
}

function ActivityDot({ lastSeenAt }: { lastSeenAt: string | null }): JSX.Element {
  if (!lastSeenAt) return <span className="text-text-tertiary">noch nie</span>;
  const ms = Date.now() - new Date(lastSeenAt).getTime();
  const days = ms / (1000 * 60 * 60 * 24);
  let color = darkTheme.colors.success;
  if (days > 7) color = darkTheme.colors.warning;
  if (days > 30) color = darkTheme.colors.danger;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} aria-hidden />
      <span>{formatRelative(lastSeenAt)}</span>
    </span>
  );
}

function StatusChip({
  suspendedAt,
  deletedAt,
  emailVerified,
}: {
  suspendedAt: string | null;
  deletedAt: string | null;
  emailVerified: boolean;
}): JSX.Element {
  if (deletedAt) {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Gelöscht
      </span>
    );
  }
  if (suspendedAt) {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
        style={{ background: darkTheme.colors.warningBg, color: darkTheme.colors.warning }}
      >
        Suspendiert
      </span>
    );
  }
  if (!emailVerified) {
    return (
      <span
        className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
        style={{ background: darkTheme.colors.bgElevated, color: darkTheme.colors.textSecondary }}
      >
        Email unbestätigt
      </span>
    );
  }
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
      style={{ background: darkTheme.colors.successBg, color: darkTheme.colors.success }}
    >
      Aktiv
    </span>
  );
}

function SkeletonTable(): JSX.Element {
  return (
    <div
      className="mt-4 rounded-md border p-3"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="mb-2 flex items-center gap-3 animate-pulse">
          <div className="h-8 w-8 rounded-full bg-bg-elevated" />
          <div className="flex-1 space-y-1">
            <div className="h-3 w-1/3 rounded bg-bg-elevated" />
            <div className="h-2 w-1/4 rounded bg-bg-elevated" />
          </div>
        </div>
      ))}
    </div>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'gerade eben';
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  if (ms < 86_400_000) return `vor ${Math.floor(ms / 3_600_000)} h`;
  if (ms < 30 * 86_400_000) return `vor ${Math.floor(ms / 86_400_000)} Tagen`;
  return new Date(iso).toLocaleDateString('de-DE');
}
